import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { DocumentDto, MonitoringComplete, MonitoringDetail, MonitoringSpeciesFailure, MonitoringValues } from '@tww/shared';
import { CONDITIONS, CONDITION_LABELS, GROWTH_BEHAVIORS, GROWTH_BEHAVIOR_LABELS, IMPROVEMENTS, IMPROVEMENT_LABELS, WILDLIFE_LABELS, WILDLIFE_STATES, fmtDate, fmtHa, fmtNum, todayIso, validateMonitoringComplete, windowLabel } from '@tww/shared';
import { EmailPanel } from '../emails/EmailPanel';
import { api, ApiRequestError, qs } from '../../api/client';
import { useLookup } from '../../api/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { ErrorState, Note, Skeleton } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { Checkbox, DateInput, Field, NumberInput, TextArea, TextInput } from '../../ui/fields';
import { useToast } from '../../ui/Toast';
import { ReviewNote } from '../../ui/ReviewNote';
import { plantingLabel } from '../areas/common';
import { DuePill, MonitoringStatusPill, monitoringTitle, useUsersPick } from './common';
import { Dropdown } from '../../ui/Dropdown';

type Draft = Omit<MonitoringValues, 'species_failures'> & { species_failures: MonitoringSpeciesFailure[] };

/** Erfassungsseite (Plan 05 Abschnitt 5.3): als Seite mit Abschnitten, Segment-Buttons, Baumarten-Tabelle, Fotos, Ergebnisvorschau; rechts die Zusammenfassung mit Speichern und Abschließen */
export function MonitoringFormPage() {
  const { id } = useParams();
  const mid = Number(id);
  const q = useQuery({ queryKey: ['monitoring', mid], queryFn: () => api.get<MonitoringDetail>(`/monitorings/${mid}`), enabled: Number.isFinite(mid) });
  if (q.isLoading) return <Skeleton rows={8} />;
  if (q.isError || !q.data) return <ErrorState error={q.error ?? new Error('Monitoring nicht gefunden')} retry={() => q.refetch()} />;
  return <Editor key={mid} m={q.data} />;
}

function toDraft(m: MonitoringDetail, initials: string | null): Draft {
  const open = m.status === 'planned' || m.status === 'in_progress';
  return {
    done_at: m.done_at ?? (open ? todayIso() : null),
    done_by: m.done_by ?? (open ? initials : null),
    condition: m.condition,
    condition_note: m.condition_note,
    growth_success: m.growth_success,
    failure_rate_pct: m.failure_rate_pct,
    growth_behavior: m.growth_behavior,
    wildlife_intact: m.wildlife_intact,
    wildlife_note: m.wildlife_note,
    improvement_needed: m.improvement_needed,
    improvement_note: m.improvement_note,
    next_steps: m.next_steps,
    agreements: m.agreements,
    remarks: m.remarks,
    secured: m.secured,
    species_failures: m.species_failures.length ? m.species_failures.map(({ species_code, failure_rate_pct, note }) => ({ species_code, failure_rate_pct, note })) : open ? m.action_species.map((s) => ({ species_code: s.species_code, failure_rate_pct: null, note: null })) : [],
  };
}

function Editor({ m }: { m: MonitoringDetail }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { can, me } = useAuth();
  const users = useUsersPick();
  const species = useLookup('tree_species').values;
  const open = m.status === 'planned' || m.status === 'in_progress';
  const canWrite = can('monitoring', 'write');
  const write = canWrite && open;
  // nach dem Abschluss bleiben nur Vermerke und Vereinbarungen änderbar (wie in der API)
  const notes = write || (canWrite && m.status === 'done');
  const win = windowLabel(m.settings?.window);
  const inquiry = m.kind === 'inquiry';
  const [d, setD] = useState<Draft>(() => toDraft(m, me?.user.initials ?? null));
  const [dirty, setDirty] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const [followup, setFollowup] = useState(true);
  const [result, setResult] = useState<{ applied: string[]; monitoring: MonitoringDetail } | null>(null);
  const updatedAt = useRef(m.updated_at);
  const set = (patch: Partial<Draft>) => {
    setD((s) => ({ ...s, ...patch }));
    setDirty(true);
  };
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['monitoring', m.id] });
    qc.invalidateQueries({ queryKey: ['monitorings'] });
    qc.invalidateQueries({ queryKey: ['area', m.area_id] });
    qc.invalidateQueries({ queryKey: ['area-monitorings', m.area_id] });
    qc.invalidateQueries({ queryKey: ['areas'] });
    qc.invalidateQueries({ queryKey: ['today'] });
    qc.invalidateQueries({ queryKey: ['monitoring-summary'] });
  };
  const payload = (): MonitoringComplete => ({ ...d, species_failures: d.species_failures.filter((f) => f.species_code), updated_at: updatedAt.current });
  const notesPayload = () => ({ wildlife_note: d.wildlife_note, condition_note: d.condition_note, growth_success: d.growth_success, improvement_note: d.improvement_note, next_steps: d.next_steps, agreements: d.agreements, remarks: d.remarks, updated_at: updatedAt.current });
  const save = useMutation({
    mutationFn: () => api.patch<MonitoringDetail>(`/monitorings/${m.id}`, open ? payload() : notesPayload()),
    onSuccess: (r) => {
      updatedAt.current = r.updated_at;
      setDirty(false);
      toast(open ? 'Entwurf gespeichert' : 'Änderungen gespeichert');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const assign = useMutation({
    mutationFn: (user: number | null) => api.patch<MonitoringDetail>(`/monitorings/${m.id}`, { assigned_user_id: user, updated_at: updatedAt.current }),
    onSuccess: (r) => {
      updatedAt.current = r.updated_at;
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const complete = useMutation({
    mutationFn: () => api.post<{ monitoring: MonitoringDetail; applied: string[] }>(`/monitorings/${m.id}/complete`, { ...payload(), create_followup: followup }),
    onSuccess: (r) => {
      setCompleteOpen(false);
      setDirty(false);
      setResult(r);
      invalidate();
    },
    onError: (e) => toast(e instanceof ApiRequestError ? [e.message, ...Object.values(e.fields ?? {})].join(' · ') : 'Abschluss fehlgeschlagen', 'error'),
  });
  const [skipReason, setSkipReason] = useState('');
  const skip = useMutation({
    mutationFn: () => api.post(`/monitorings/${m.id}/skip`, { reason: skipReason }),
    onSuccess: () => {
      toast('Monitoring entfallen');
      setSkipOpen(false);
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (dirty && notes) e.preventDefault();
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty, notes]);

  const errors = write ? (inquiry ? (d.done_at ? {} : { done_at: 'Datum fehlt' }) : validateMonitoringComplete(d)) : {};
  const err = (k: string) => (errors as Record<string, string>)[k];
  const phone = m.forester?.mobile ?? m.forester?.phone;
  const seg = (label: string, value: string | null | undefined, onChange: (v: string | null) => void, options: { value: string; label: string }[], opts: { error?: string; hint?: string; required?: boolean } = {}) => (
    <Field label={label} error={opts.error} hint={opts.hint} required={opts.required} full={options.reduce((n, o) => n + o.label.length + 4, 0) > 34}>
      <div className="seg lg" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={value === o.value ? 'on' : ''} disabled={!write} onClick={() => write && onChange(value === o.value ? null : o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    </Field>
  );
  const opt = <T extends string>(codes: readonly T[], labels: Record<T, string>) => codes.map((c) => ({ value: c, label: labels[c] }));
  const title = monitoringTitle(m);

  return (
    <>
      <div className="crumb">
        <Link to="/monitoring">Monitoring</Link> / <Link to={`/flaechen/${m.area_id}`}>{m.area_name}</Link>
      </div>
      <div className="head">
        <div>
          <h1>
            {title} · {m.area_name}
          </h1>
          <div className="sub">
            {[m.planting_year ? `Pflanzung ${plantingLabel(m)}` : null, m.area_hectares != null ? `${fmtHa(m.area_hectares)} ha` : null, m.area_trees ? `${fmtNum(m.area_trees)} Bäume` : null, m.partner ? `Partner ${m.partner.display_name}` : null, [m.state_label, m.region].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div className="actions">
          <DuePill due={m.due} status={m.status} />
          <a className="btn" href={`/api/v1/monitorings/${m.id}/print`} target="_blank" rel="noreferrer">
            <Icon name="doc" /> Druckansicht
          </a>
          <Link className="btn" to={`/flaechen/${m.area_id}`}>
            Zur Fläche
          </Link>
        </div>
      </div>
      {m.status === 'done' && <Note kind="good">Erledigt am {fmtDate(m.done_at)}{m.done_by ? ` durch ${m.done_by}` : ''}{m.secured ? ' · Kultur gesichert' : ''}.{canWrite ? ' Vermerke und Vereinbarungen bleiben änderbar.' : ''}</Note>}
      {m.status === 'skipped' && <Note>Entfallen{m.skip_reason ? `: ${m.skip_reason}` : ''}.</Note>}
      {m.review_needed && <ReviewNote entity="monitorings" id={m.id} queryKey={['monitoring', m.id]} canWrite={canWrite} />}

      <div className="page-form">
        <div className="col">
          <section className="section">
            <div className="sh">
              <h2>1. Termin</h2>
              <span className="desc">Zeitraum {win} {m.due_year}</span>
            </div>
            <div className="sb">
              <div className="form">
                <DateInput label="Datum" value={d.done_at} onChange={(v) => set({ done_at: v || null })} required error={err('done_at')} disabled={!write} />
                <TextInput label="Durchgeführt von" value={d.done_by} onChange={(v) => set({ done_by: v })} required={!inquiry} error={err('done_by')} hint="Kürzel" disabled={!write} />
                <div className="full picker-info mon-forester" style={{ marginTop: 0 }}>
                  {m.forester ? (
                    <>
                      <span>
                        Förster{' '}
                        <Link className="link" to={`/kontakte/${m.forester.id}`}>
                          {m.forester.display_name}
                        </Link>
                      </span>
                      {phone && <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a>}
                      {m.forester.email && <a href={`mailto:${m.forester.email}`}>{m.forester.email}</a>}
                    </>
                  ) : (
                    'Kein Förster zugeordnet'
                  )}
                </div>
              </div>
            </div>
          </section>

          {inquiry ? (
            <section className="section">
              <div className="sh">
                <h2>2. Nachfrage beim Förster</h2>
                <span className="desc">ohne Vor-Ort-Termin</span>
              </div>
              <div className="sb">
                <div style={{ marginBottom: 12 }}>
                  <EmailPanel entityType="monitoring" entityId={m.id} prepareBase={`/monitorings/${m.id}`} title="Nachfrage-E-Mail" />
                </div>
                <div className="form">
                  <TextArea label="Rückmeldung des Försters / Vereinbarungen" value={d.agreements} onChange={(v) => set({ agreements: v })} rows={4} disabled={!notes} />
                  <TextArea label="Vermerke" value={d.remarks} onChange={(v) => set({ remarks: v })} rows={2} disabled={!notes} />
                </div>
              </div>
            </section>
          ) : (
            <>
              <section className="section">
                <div className="sh">
                  <h2>2. Wildschutz</h2>
                </div>
                <div className="sb">
                  <div className="form">
                    {seg('Wildschutz intakt', d.wildlife_intact, (v) => set({ wildlife_intact: (v as Draft['wildlife_intact']) ?? null }), opt(WILDLIFE_STATES, WILDLIFE_LABELS), { error: err('wildlife_intact'), required: true })}
                    <TextArea label="Vermerk" value={d.wildlife_note} onChange={(v) => set({ wildlife_note: v })} rows={2} disabled={!notes} />
                  </div>
                </div>
              </section>
              <section className="section">
                <div className="sh">
                  <h2>3. Anwuchs</h2>
                </div>
                <div className="sb">
                  <div className="form">
                    <NumberInput label="Ausfallrate gesamt in Prozent" value={d.failure_rate_pct ?? null} onChange={(v) => set({ failure_rate_pct: v })} decimals={0} error={err('failure_rate_pct')} disabled={!write} />
                    <Field label="Ausfallrate je Baumart" full hint="optional">
                      <div className="species-rows">
                        {d.species_failures.map((row, i) => (
                          <div className="r" key={i}>
                            <Dropdown value={row.species_code} aria-label="Baumart" disabled={!write} onChange={(v) => set({ species_failures: d.species_failures.map((x, j) => (j === i ? { ...x, species_code: v } : x)) })} placeholder="Baumart" options={species.map((s) => ({ value: String(s.code), label: s.label }))} />
                            <input type="number" inputMode="numeric" min={0} max={100} placeholder="%" aria-label="Ausfallrate in Prozent" value={row.failure_rate_pct ?? ''} disabled={!write} onChange={(e) => set({ species_failures: d.species_failures.map((x, j) => (j === i ? { ...x, failure_rate_pct: e.target.value === '' ? null : Number(e.target.value) } : x)) })} />
                            {write && (
                              <button type="button" className="icon-btn" aria-label="Zeile entfernen" onClick={() => set({ species_failures: d.species_failures.filter((_, j) => j !== i) })}>
                                <Icon name="x" />
                              </button>
                            )}
                          </div>
                        ))}
                        {write && (
                          <div>
                            <button type="button" className="btn sm" onClick={() => set({ species_failures: [...d.species_failures, { species_code: '', failure_rate_pct: null, note: null }] })}>
                              <Icon name="plus" /> Baumart
                            </button>
                          </div>
                        )}
                      </div>
                    </Field>
                    <TextArea label="Anwuchserfolg" value={d.growth_success} onChange={(v) => set({ growth_success: v })} rows={2} disabled={!notes} />
                  </div>
                </div>
              </section>
              <section className="section">
                <div className="sh">
                  <h2>4. Wuchsverhalten und Flächenzustand</h2>
                </div>
                <div className="sb">
                  <div className="form">
                    {seg('Wuchsverhalten', d.growth_behavior, (v) => set({ growth_behavior: (v as Draft['growth_behavior']) ?? null }), opt(GROWTH_BEHAVIORS, GROWTH_BEHAVIOR_LABELS))}
                    {seg('Flächenzustand insgesamt', d.condition, (v) => set({ condition: (v as Draft['condition']) ?? null }), opt(CONDITIONS, CONDITION_LABELS), { error: err('condition'), required: true })}
                    <TextArea label="Notiz zum Flächenzustand" value={d.condition_note} onChange={(v) => set({ condition_note: v })} rows={2} disabled={!notes} />
                  </div>
                </div>
              </section>
              <section className="section">
                <div className="sh">
                  <h2>5. Nachbesserung</h2>
                </div>
                <div className="sb">
                  <div className="form">
                    {seg('Nachbesserung erforderlich', d.improvement_needed, (v) => set({ improvement_needed: (v as Draft['improvement_needed']) ?? null }), opt(IMPROVEMENTS, IMPROVEMENT_LABELS), { hint: d.improvement_needed === 'ja' ? 'Beim Abschluss wird ein zusätzliches Monitoring im Folgejahr vorgeschlagen' : undefined })}
                    <TextArea label="Notiz" value={d.improvement_note} onChange={(v) => set({ improvement_note: v })} rows={2} placeholder="z. B. 1.000 Bäume sollen nachgebessert werden" disabled={!notes} />
                    <TextArea label="Weiteres Vorgehen" value={d.next_steps} onChange={(v) => set({ next_steps: v })} rows={2} disabled={!notes} />
                  </div>
                </div>
              </section>
              <section className="section">
                <div className="sh">
                  <h2>6. Vereinbarungen</h2>
                </div>
                <div className="sb">
                  <div className="form">
                    <TextArea label="Vereinbarungen mit Förster oder Baumschule" value={d.agreements} onChange={(v) => set({ agreements: v })} rows={3} disabled={!notes} />
                    <TextArea label="Vermerke" value={d.remarks} onChange={(v) => set({ remarks: v })} rows={2} disabled={!notes} />
                  </div>
                </div>
              </section>
              <PhotoSection monitoringId={m.id} canWrite={canWrite} />
              <section className="section">
                <div className="sh">
                  <h2>8. Ergebnis</h2>
                </div>
                <div className="sb">
                  <Checkbox value={Boolean(d.secured)} onChange={(v) => set({ secured: v })} label="Kultur ist gesichert, Fläche aus dem Monitoring nehmen" disabled={!write} hint="Offene Termine und Nachfragen dieser Fläche entfallen" />
                  <Note kind="info">{d.secured ? 'Kultur gesichert, Fläche wird aus dem Monitoring genommen' : m.next_preview.label}</Note>
                </div>
              </section>
            </>
          )}
        </div>

        <aside className="aside">
          <div className="panel">
            <div className="pb summary-box">
              <dl>
                <dt>Status</dt>
                <dd>
                  <MonitoringStatusPill status={m.status} />
                </dd>
                <dt>Fällig</dt>
                <dd>
                  {m.due_year} · {win}
                  {m.due.kind === 'overdue' && <span className="miss"> · überfällig</span>}
                </dd>
                <dt>Zuständig</dt>
                <dd>
                  {write ? (
                    <Dropdown aria-label="Zuständig" value={m.assigned?.id ?? ''} onChange={(v) => assign.mutate(v ? Number(v) : null)} placeholder="– niemand –" options={(users.data ?? []).map((u) => ({ value: String(u.id), label: `${u.initials ? `${u.initials} · ` : ''}${u.display_name}` }))} />
                  ) : (
                    (m.assigned?.display_name ?? '–')
                  )}
                </dd>
                {m.origin_action && (
                  <>
                    <dt>Pflanzaktion</dt>
                    <dd>
                      <Link className="link" to={`/aktionen/${m.origin_action.id}`}>
                        {m.origin_action.code}
                      </Link>
                    </dd>
                  </>
                )}
                {m.last && (
                  <>
                    <dt>Letzter Befund</dt>
                    <dd>
                      {m.last.condition_label} ({m.last.year})
                    </dd>
                  </>
                )}
                <dt>Danach</dt>
                <dd>{d.secured ? 'Kultur gesichert' : m.next_preview.label}</dd>
              </dl>
            </div>
            {notes && (
              <div className="pb actions-col" style={{ paddingTop: 0 }}>
                <button type="button" className="btn" onClick={() => save.mutate()} disabled={save.isPending || !dirty}>
                  {m.status === 'done' ? 'Änderungen speichern' : 'Entwurf speichern'}
                </button>
                {write && (
                  <button type="button" className="btn primary" onClick={() => setCompleteOpen(true)}>
                    {inquiry ? 'Nachfrage abschließen' : 'Abschließen'}
                  </button>
                )}
                {write && (
                  <button type="button" className="btn" onClick={() => setSkipOpen(true)}>
                    Entfallen lassen
                  </button>
                )}
              </div>
            )}
            {write && Object.keys(errors).length > 0 && (
              <div className="pb" style={{ paddingTop: 0 }}>
                <div className="form-errors">
                  Für den Abschluss fehlt noch:
                  <ul>
                    {Object.values(errors).map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>

      {completeOpen && (
        <Modal
          title={inquiry ? 'Nachfrage abschließen' : `${title} abschließen`}
          subtitle={m.area_name}
          onClose={() => setCompleteOpen(false)}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setCompleteOpen(false)}>
                Abbrechen
              </button>
              <button className="btn primary" type="button" disabled={Object.keys(errors).length > 0 || complete.isPending} onClick={() => complete.mutate()}>
                Abschließen
              </button>
            </>
          }
        >
          {Object.keys(errors).length > 0 ? (
            <Note kind="bad">Es fehlen noch: {Object.values(errors).join(', ')}</Note>
          ) : (
            <>
              <p style={{ marginTop: 0 }}>Beim Abschluss passiert:</p>
              <ul className="applied">
                <li>Status erledigt am {fmtDate(d.done_at)}{d.done_by ? ` durch ${d.done_by}` : ''}</li>
                {d.secured ? (
                  <li>Fläche wird als „Gesichert“ markiert, offene Termine und Nachfragen entfallen</li>
                ) : (
                  <li>{m.next_preview.label}</li>
                )}
                {!inquiry && m.origin_action && <li>QS-Punkt „Fläche kontrolliert / besichtigt“ der Aktion {m.origin_action.code} erhält das Datum</li>}
                {!inquiry && d.improvement_needed === 'ja' && !d.secured && (
                  <li>
                    <label className="radios" style={{ paddingTop: 0 }}>
                      <input type="checkbox" checked={followup} onChange={(e) => setFollowup(e.target.checked)} /> Zusätzliches Monitoring {m.due_year + 1} anlegen (Nachbesserung erforderlich)
                    </label>
                    {m.origin_action && <span className="sm mut" style={{ display: 'block' }}>QS-Punkt „Maßnahme“ der Aktion wird mit Notiz geöffnet</span>}
                  </li>
                )}
              </ul>
            </>
          )}
        </Modal>
      )}
      {skipOpen && (
        <Modal
          title="Monitoring entfallen lassen"
          subtitle={m.area_name}
          onClose={() => setSkipOpen(false)}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setSkipOpen(false)}>
                Abbrechen
              </button>
              <button className="btn danger" type="button" disabled={!skipReason.trim() || skip.isPending} onClick={() => skip.mutate()}>
                Entfallen lassen
              </button>
            </>
          }
        >
          <div className="form one">
            <TextArea label="Grund" value={skipReason} onChange={setSkipReason} rows={3} hint="Der Grund bleibt in der Historie der Fläche sichtbar" />
          </div>
        </Modal>
      )}
      {result && (
        <Modal
          title={inquiry ? 'Nachfrage abgeschlossen' : 'Monitoring abgeschlossen'}
          onClose={() => setResult(null)}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setResult(null)}>
                Hier bleiben
              </button>
              <button className="btn primary" type="button" onClick={() => nav(`/flaechen/${m.area_id}`)}>
                Zur Flächenakte
              </button>
            </>
          }
        >
          <ul className="applied">
            {result.applied.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </Modal>
      )}
    </>
  );
}

/** Fotos direkt vom Tablet oder Smartphone (M-07): Art „Foto“, hängen am Monitoring und erscheinen bei der Fläche unter Dateien */
function PhotoSection({ monitoringId, canWrite }: { monitoringId: number; canWrite: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['documents', 'monitoring', monitoringId];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<{ items: DocumentDto[] }>(`/documents${qs({ entity_type: 'monitoring', entity_id: monitoringId })}`).then((r) => r.items) });
  const input = useRef<HTMLInputElement>(null);
  const up = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) {
        const fd = new FormData();
        fd.set('entity_type', 'monitoring');
        fd.set('entity_id', String(monitoringId));
        fd.set('kind', 'foto');
        fd.set('file', f);
        await api.upload('/documents', fd);
      }
    },
    onSuccess: (_d, files) => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ['monitoring', monitoringId] });
      toast(`${files.length} Foto${files.length > 1 ? 's' : ''} angehängt`);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const [confirmDel, setConfirmDel] = useState<DocumentDto | null>(null);
  const del = useMutation({
    mutationFn: (id: number) => api.post(`/documents/${id}/archive`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ['monitoring', monitoringId] });
      setConfirmDel(null);
      toast('Foto entfernt');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <section className="section">
      {confirmDel && <ConfirmDialog title="Foto entfernen" text={<>„{confirmDel.original_name}“ wird aus dem Monitoring entfernt.</>} confirmLabel="Entfernen" danger onConfirm={() => del.mutate(confirmDel.id)} onClose={() => setConfirmDel(null)} busy={del.isPending} />}
      <div className="sh">
        <h2>7. Fotos</h2>
      </div>
      <div className="sb">
        {q.isLoading ? (
          <Skeleton rows={2} />
        ) : (
          <div className="photo-grid">
            {(q.data ?? []).map((d) => (
              <div key={d.id} style={{ position: 'relative' }}>
                <a href={`/api/v1/documents/${d.id}/file`} target="_blank" rel="noreferrer">
                  <img src={d.has_thumb ? `/api/v1/documents/${d.id}/thumb` : `/api/v1/documents/${d.id}/file`} alt={d.original_name} />
                </a>
                {canWrite && (
                  <button type="button" className="icon-btn" style={{ position: 'absolute', top: 2, right: 2, background: 'var(--surface)' }} aria-label="Foto entfernen" title="Foto entfernen" onClick={() => setConfirmDel(d)}>
                    <Icon name="x" />
                  </button>
                )}
              </div>
            ))}
            {canWrite && (
              <div className="add" role="button" tabIndex={0} onClick={() => input.current?.click()} onKeyDown={(e) => e.key === 'Enter' && input.current?.click()}>
                <span>
                  <Icon name="photo" />
                  <br />
                  {up.isPending ? 'wird hochgeladen …' : 'Foto aufnehmen oder auswählen'}
                </span>
              </div>
            )}
          </div>
        )}
        <input ref={input} type="file" accept="image/*" capture="environment" multiple hidden onChange={(e) => e.target.files && up.mutate(Array.from(e.target.files))} />
      </div>
    </section>
  );
}
